import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useSource } from "@/context/SourceContext";
import { triggerHaptic } from "@/lib/haptics";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Globe, Package, Loader2, ChevronRight, Check } from "lucide-react";

export default function SourceSelectDialog({ open = true, onClose }) {
  const { source, setSource } = useSource();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const forced = !onClose;

  useEffect(() => {
    api.get("/orders")
      .then((r) => setSources(Array.from(new Set((r.data || []).map((o) => o.source_portal).filter(Boolean)))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const choose = (s) => { triggerHaptic("success"); setSource(s); onClose?.(); };

  const Option = ({ value, title, hint, icon: Icon, primary, index, testId }) => {
    const active = source === value;
    return (
      <motion.button type="button" onClick={() => choose(value)} data-testid={testId}
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05, duration: 0.3 }}
        whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}
        className={`group w-full flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all duration-200 ${primary ? "border-primary/30 bg-gradient-to-r from-primary/[.07] to-cyan-500/[.05] hover:border-primary shadow-[0_10px_30px_-18px_rgba(33,79,216,.6)]" : "border-border bg-white/80 hover:border-primary/40 hover:bg-primary/[.03]"} ${active ? "ring-2 ring-primary/30" : ""}`}>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 transition-transform duration-300 group-hover:scale-110 ${primary ? "bg-primary text-white ring-primary/30 shadow-lg shadow-primary/30" : "bg-slate-100 text-slate-600 ring-slate-200"}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold tracking-tight">{title}</div>
          {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        </div>
        {active ? <Check className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />}
      </motion.button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className={`sm:max-w-md max-h-[88vh] overflow-y-auto ${forced ? "[&>button]:hidden" : ""}`} data-testid="source-select-dialog">
        <DialogHeader>
          <div className="mx-auto sm:mx-0 mb-1 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-mono-label font-semibold uppercase tracking-[0.18em] text-primary w-fit">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-primary" /></span>
            Workspace
          </div>
          <DialogTitle className="text-2xl">Choose a source</DialogTitle>
          <DialogDescription>Pick <span className="font-semibold text-foreground">ShipOMS</span> to work across every country, or focus on a single source portal.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-2">
            <Option value="ALL" title="ShipOMS — All Sources" hint="Show shipments across every country" icon={Globe} primary index={0} testId="source-option-all" />
            {sources.map((s, i) => <Option key={s} value={s} title={s} icon={Package} index={i + 1} testId={`source-option-${s}`} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
